import type { PublicState } from './protocol';
import type { Team } from './series';

// ============================================================
// v29 BOLT-169 — WHO STARTS THE NEXT ROUND, derived once.
//
// The bug this exists to prevent ended games in the field. BOLT-159 moved the
// between-rounds control from the host to the round's HIDER and enforced it on
// the server (`index.ts` → `session.starterFor`). Score.tsx followed.
// ScoreView.swift did not. So the host saw a button the server answered with
// `forbidden`, the actual next hider saw "Waiting for the host…" and no button,
// and NOBODY could start the round. Roles alternate, so it deadlocked every
// round the host was not hiding — Jake had to quit and recreate the game
// repeatedly.
//
// That is the fourth time a change has landed on web without its Swift half
// (v24 landmarks, v25 Phase 1, BOLT-169, BOLT-175). The pattern is nastier than
// it looks: the SERVER ships with the web, so the server starts enforcing a
// rule the phone does not know about, and the failure is a deadlock rather than
// a cosmetic gap.
//
// So the rule lives HERE, in one pure function over PublicState, and the Swift
// port mirrors it line for line (`ios/Bolt/Play/RoundStart.swift`). The parity
// guard in `roundStartParity.test.ts` fails if they drift.
//
// It deliberately mirrors `session.starterFor` rather than inventing a second
// rule, and it derives from `series.history` rather than from a new wire field,
// because history is what the score on the same screen is derived from — the
// control cannot disagree with what the player is reading.
// ============================================================

/** Who may press "Begin head start" between rounds. */
export type RoundStarter =
  /** The host — practice, or a room below the ready-gate floor (the v28 path). */
  | { kind: 'host' }
  /** Whichever team hides NEXT. */
  | { kind: 'team'; team: Team }
  /** Not yet knowable (no completed round to alternate from). */
  | { kind: 'unknown' };

/**
 * Mirrors `Session.starterFor(roundIndex + 1)`.
 *
 * Practice is one human against a bot: there is no one to hand it to.
 * Below the ready-gate floor an older build never learns it is the hider's turn
 * to press start, so the host keeps that job — the BOLT-159 mixed-room
 * fallback, which this must not break.
 */
export function nextRoundStarter(state: PublicState): RoundStarter {
  if (state.practice === true) return { kind: 'host' };
  if (state.readyGateActive !== true) return { kind: 'host' };
  const history = state.series?.history ?? [];
  const last = history[history.length - 1];
  if (!last?.hiderTeam) return { kind: 'unknown' };
  // Roles alternate: whoever did NOT hide the round that just ended hides next.
  return { kind: 'team', team: last.hiderTeam === 'A' ? 'B' : 'A' };
}

/** Is the viewing player the one who starts the next round? */
export function youStartNextRound(state: PublicState): boolean {
  const starter = nextRoundStarter(state);
  if (starter.kind === 'host') return state.you.isHost === true;
  if (starter.kind === 'team') return state.you.team === starter.team;
  return false;
}
